/**
 * GradeBenefitPresenter
 * --
 */
import React from 'react';
import { Content, Row, Col, Title, Text, ModalLayout } from 'components';
import {
  Avatar,
  Button,
  Descriptions,
  Flex,
  InputNumber,
  Select,
  Spin,
  Table,
  Tabs,
  Input,
  Space,
  Tag,
} from 'antd';
import {
  PictureOutlined,
  RestOutlined,
  ShoppingCartOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { STORE_URL, stringToMoneyFormat } from 'utils';
import dayjs from 'dayjs';
import {
  APPLICATION_STATUS,
  PRODUCT_STATUS,
  PRODUCT_TYPE,
} from 'constants/ProductConstants';
import { MENU_TYPE } from 'constants/MenuConstants';
import CONSTANTS from 'constants';

const DATE_FORMAT = 'YYYY-MM-DD';

const TYPE_OPTION = [
  {
    label: '원',
    value: 'W',
  },
  {
    label: '%',
    value: 'R',
  },
];

const GradeBenefitPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    initialData,
    grades,
    products,
    menus,
    benefit,
    tabKey,
    isLoading,
    selected,
    categories,
    shops,
    isOpened,
    searchInput,
    searchedColumn,

    //
    onChange,
    onSelect,
    onTabChange,
    onSubmit,
    onToggle,
    onSearchedColumn,
  } = props;

  /* ===== STATE ===== */
  /* ===== VARIABLE ===== */
  /**
   * 탭 아이템
   * --
   */
  const tabItems = [
    {
      key: 'product',
      label: '상품',
      value: 'product',
      icon: <ShoppingCartOutlined />,
    },
    {
      key: 'menu',
      label: '메뉴',
      value: 'menu',
      icon: <RestOutlined />,
    },
  ];

  /* ===== FUNCTION ===== */
  const handleTableSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleTableReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`검색어를 입력해주세요`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleTableSearch(selectedKeys, confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleTableSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            검색
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleTableReset(clearFilters, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            초기화
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex.replace(/^processed_/, '').replace(/^expired_/, '')]
        ?.toString()
        ?.toLowerCase()
        ?.includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) => text,
  });

  /* ===== HOOKS ===== */
  const columns = React.useMemo(() => {
    // 공통
    let col = [
      {
        key: 'name',
        title: '상품명',
        dataIndex: `${tabKey}_name`,
        fixed: 'left',
        ...getColumnSearchProps(`${tabKey}_name`),
        render: (item, record) => (
          <Flex>
            <Avatar
              shape="square"
              size={64}
              src={`${STORE_URL}/${record.thumbnail}`}
              icon={<PictureOutlined />}
            />

            <Flex style={{ padding: 12 }}>
              <Text size={14} weight={700}>
                {item}
              </Text>
            </Flex>
          </Flex>
        ),
      },
      {
        key: 'category',
        title: '분류',
        dataIndex: `product_category`,
        render: (item) => item.product_category_name,
        filters: categories
          .filter((cate) => cate.type === tabKey.toLocaleUpperCase())
          .map((cate) => ({
            text: cate.product_category_name,
            value: cate.product_category_id,
          })),
        onFilter: (value, record) => record.product_category_id === value,
      },
      {
        key: 'price',
        title: '가격',
        dataIndex: `price`,
        render: (item) => stringToMoneyFormat(item, '원'),
        sorter: (a, b) => a.price - b.price,
      },

      {
        key: 'created_at',
        title: '등록일',
        dataIndex: `created_at`,
        render: (item) => dayjs.unix(item).format(DATE_FORMAT),
        sorter: (a, b) => a.created_at - b.created_at,
      },
      {
        key: 'type',
        title: '유형',
        dataIndex: `type`,
        render: (type) => {
          const TYPES = tabKey === 'product' ? PRODUCT_TYPE : MENU_TYPE;
          return <Tag color={TYPES[type]?.color}>{TYPES[type]?.title}</Tag>;
        },
        // filters: Object.entries(
        //   tabKey === 'product' ? PRODUCT_TYPE : MENU_TYPE
        // ).map(([key, value]) => ({
        //   text: value.title,
        //   value: key,
        // })),
        // onFilter: (value, record) => record.type === value,
        // onFilter: (value, record) => record.type === value,
      },
    ];

    let newItems = [];
    if (tabKey === 'product') {
      newItems = [
        {
          key: 'shop',
          title: '업체명',
          dataIndex: `shop_information`,
          render: (item) => item.shop_information_name,
          filters: shops.map((shop) => ({
            text: shop.shop_information_name,
            value: shop.shop_information_id,
          })),
          onFilter: (value, record) => record.shop_information_id === value,
        },
        {
          key: 'num',
          title: '재고수량',
          dataIndex: `num`,
          render: (item) => stringToMoneyFormat(item, 'EA'),
          sorter: (a, b) => a.num - b.num,
        },
        {
          key: 'product_status',
          title: '판매상태',
          dataIndex: `product_status`,
          render: (item) => PRODUCT_STATUS[item]?.title,
          filters: Object.values(PRODUCT_STATUS)?.map((item) => ({
            text: item.title,
            value: item.value,
          })),
          onFilter: (value, record) => record.product_status === value,
        },
        {
          key: 'application_status',
          title: '심사상태',
          dataIndex: `application_status`,
          render: (item) => APPLICATION_STATUS[item]?.title,
          filters: Object.entries(PRODUCT_STATUS)?.map(([key, value]) => ({
            text: value.title,
            value: key,
          })),
          onFilter: (value, record) => record.product_status === value,
        },
      ];
    } else {
    }

    return [
      ...col,
      ...newItems,
      {
        key: 'grade_benefit_info',
        title: '혜택',
        dataIndex: `grade_benefit_info`,
        render: (item) =>
          grades.map((grade) => {
            const gradeBenefit = item
              ? item[grade.grade_id] ?? initialData
              : initialData;

            return (
              <Flex gap={4}>
                <Text weight={700}>
                  {grade.grade_name}
                  {' - '}
                </Text>
                <Flex gap={4}>
                  <Text>
                    할인: {gradeBenefit.discount_rate}
                    {CONSTANTS.DISCOUNT_TYPE[gradeBenefit.discount_type]}
                  </Text>
                  <Text>/</Text>
                  <Text>
                    적립: {gradeBenefit.reserve_rate}
                    {CONSTANTS.DISCOUNT_TYPE[gradeBenefit.reserve_type]}
                  </Text>
                </Flex>
              </Flex>
            );
          }),
      },
      {
        key: '#',
        title: '#',
        dataIndex: `#`,
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Button
            onClick={() => {
              onSelect([record]);
              onToggle(record.grade_benefit_info);
            }}
          >
            혜택수정
          </Button>
        ),
      },
    ];
    // eslint-disable-next-line
  }, [tabKey, categories, shops, grades, initialData]);

  const tableData = React.useMemo(() => {
    const items = {
      product: products,
      menu: menus,
    };
    let data = items[tabKey];

    const searchValues = Object.entries(searchedColumn)?.filter(
      ([_, value]) => value !== null && value !== undefined
    );

    if (searchValues?.length) {
      data = data.filter((item) => {
        return searchValues.every(([key, value]) => {
          const keys = {
            name: `${tabKey}_${key}`,
            type: key,
            category: `product_category_id`,
          };

          if (key === 'name') {
            return item[keys[key]]?.includes(value[0] || '');
          }
          if (key === 'category') {
            return value.includes(item[keys[key]]);
          }
          if (key === 'type') {
            return value.includes(item[keys[key]]);
          }

          return true;
        });
      });
    }

    return data;
  }, [tabKey, products, menus, searchedColumn]);

  /**
   * 검색 및 필터된 데이터 함수
   * --
   * @param {*} _
   * @param {*} filters
   */
  const handleTableChange = (_, filters) => {
    onSearchedColumn({
      name: filters.name,
      type: filters.type,
      category: filters.category,
    });
  };

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Title size={3} style={{ marginBottom: 13 }}>
            등급별 혜택 관리
          </Title>

          <Content maxWidth={'100%'} backgroundColor={'#fff'}>
            <Row>
              <Col x={24}>
                <Tabs
                  size="large"
                  activeKey={tabKey}
                  items={tabItems}
                  onChange={(key) => onTabChange(key)}
                  tabBarExtraContent={{
                    right: (
                      <Flex gap={4}>
                        <Button
                          onClick={() => onToggle()}
                          disabled={selected.length ? false : true}
                        >
                          {selected.length}건 일괄 수정
                        </Button>
                        <Button
                          onClick={() => onSelect(tableData)}
                          disabled={tableData?.length ? false : true}
                        >
                          검색된 상품 전체 선택
                        </Button>
                        <Button
                          onClick={() => onSelect([])}
                          disabled={selected.length ? false : true}
                        >
                          검색된 상품 전체 선택 해제
                        </Button>
                      </Flex>
                    ),
                  }}
                />

                <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>
                  검색된 상품 총 <span>{tableData?.length}</span>건
                </h4>

                <Table
                  key={tabKey}
                  dataSource={tableData}
                  columns={columns}
                  rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selected?.map((item) => item.key),
                    onChange: (_, selected) => onSelect(selected),
                  }}
                  scroll={{ x: 'max-content' }}
                  onChange={handleTableChange}
                />
              </Col>
            </Row>
          </Content>
        </Content>
      </Spin>

      <ModalLayout
        width={500}
        centered
        closable
        onCancel={onToggle}
        open={isOpened}
        footer={
          <Flex align="center" justify="center">
            <Button type="primary" onClick={onSubmit}>
              저장
            </Button>
          </Flex>
        }
      >
        <Content maxWidth={'100%'}>
          <Flex vertical>
            <Descriptions
              title={'등급 혜택 설정'}
              bordered
              column={1}
              items={grades.map((grade) => ({
                key: grade.grade_id,
                label: grade.grade_name,
                children: (
                  <Flex gap={4} vertical>
                    <Flex align="center" gap={4}>
                      <Text size={12}>할인</Text>

                      <Flex gap={4}>
                        <InputNumber
                          value={benefit[grade.grade_id]?.discount_rate}
                          onChange={(e) =>
                            onChange(grade.grade_id, 'discount_rate', e)
                          }
                          min={0}
                          max={
                            benefit[grade.grade_id]?.discount_type === 'R'
                              ? 100
                              : Infinity
                          }
                        />
                        <Select
                          options={TYPE_OPTION}
                          value={benefit[grade.grade_id]?.discount_type}
                          onChange={(e) =>
                            onChange(grade.grade_id, 'discount_type', e)
                          }
                        />
                      </Flex>
                    </Flex>

                    <Flex align="center" gap={4}>
                      <Text size={12}>적립</Text>

                      <Flex gap={4}>
                        <InputNumber
                          value={benefit[grade.grade_id]?.reserve_rate}
                          onChange={(e) =>
                            onChange(grade.grade_id, 'reserve_rate', e)
                          }
                          min={0}
                          max={
                            benefit[grade.grade_id]?.reserve_type === 'R'
                              ? 100
                              : Infinity
                          }
                        />
                        <Select
                          options={TYPE_OPTION}
                          value={benefit[grade.grade_id]?.reserve_type}
                          onChange={(e) =>
                            onChange(grade.grade_id, 'reserve_type', e)
                          }
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                ),
              }))}
            />
          </Flex>
        </Content>
      </ModalLayout>
    </>
  );
};

export default GradeBenefitPresenter;
